import { useMemo, useState } from "react";
import cx from "classnames";
import { Button } from "@radix-ui/themes";
import { ViewVerticalIcon, ReaderIcon } from "@radix-ui/react-icons";
import TicketOverview from "./TicketOverview";
import styles from "./TicketQAScore.module.scss";
import { TicketExploreRow } from "../../../api/useExplore/Explore.model";
import { TicketComments } from "./TicketQAScore.model";
import TicketTranscript from "./TicketTranscript";
import { useQualityTracking } from "../useQualityTracking";
import { useTicketTranscript } from "@/api/ticket-transcript/TicketTranscript.api";
import { TicketTranscript as NewTicketTranscript } from "@/features/ticket-transcript/TicketTranscript";
import { CommentValue } from "@/api/ticket-transcript/TicketTranscript.model";

const sidePanelTabs = ["overview", "transcript"] as const;
export type SidePanelTab = (typeof sidePanelTabs)[number];

function mutator(x: TicketComments): CommentValue {
  return {
    externalId: x.externalId,
    createdAt: x.createdAt,
    updatedAt: x.updatedAt,
    authorId: x.authorId,
    authorEmail: x.email,
    agent: x.agent,
    isInternal: x.isInternal,
    body: x.body,
    rawBody: x.rawBody,
    suggestedReplies: x.suggestedReplies,
  };
}

interface SidePanelProps {
  ticket: TicketExploreRow;
  comments: TicketComments[];
  hasRelease?: boolean;
  defaultTab?: SidePanelTab;
}

function SidePanel({
  ticket,
  comments,
  hasRelease,
  defaultTab = "overview",
}: SidePanelProps) {
  const { trackTrackerViewed } = useQualityTracking();

  const transcriptQuery = useTicketTranscript(ticket.id);

  const [activeTab, setActiveTab] = useState<"overview" | "transcript">(
    defaultTab
  );

  const transcriptData = useMemo(() => {
    const transcriptData = transcriptQuery.data?.comments;
    if (transcriptData) {
      return transcriptData;
    }

    if (!transcriptQuery.isFetched && comments.length > 0) {
      return comments.map(mutator);
    }

    return [];
  }, [transcriptQuery.data, transcriptQuery.isFetched, comments]);

  return (
    <div className={styles.SidePanel}>
      <div className={styles.Tabs}>
        <Button
          variant={"outline"}
          color={"gray"}
          size={"1"}
          onClick={() => setActiveTab("overview")}
          className={cx(styles.TabButton, {
            [styles.active]: activeTab === "overview",
          })}
        >
          <ViewVerticalIcon /> Overview
        </Button>

        <Button
          variant={"outline"}
          color={"gray"}
          size={"1"}
          onClick={() => {
            setActiveTab("transcript");
            trackTrackerViewed(ticket.id, 'transcript');
          }}
          className={cx(styles.TabButton, {
            [styles.active]: activeTab === "transcript",
          })}
        >
          <ReaderIcon /> Transcript
        </Button>
      </div>

      <div className={styles.TabContent}>
        {activeTab === "overview" && ticket.overview && (
          <TicketOverview {...ticket} hasRelease={hasRelease} />
        )}

        {activeTab === "transcript" && (
          <NewTicketTranscript
            data={transcriptData}
            isLoading={transcriptQuery.isLoading}
            collaborators={ticket.collaborators}
          />
        )}
      </div>
    </div>
  );
}

export default SidePanel;
